import React, { useState } from 'react';
import PropTypes from 'prop-types';
import StatusBadge from '../common/StatusBadge';
import Modal from '../common/Modal';
import FormField from '../common/FormField';
import { formatDateTime } from '../../utils/formatters';
import { CONSENT_CHECK_TYPES } from '../../utils/constants';
import './ConsentHistory.css';

const ConsentHistory = ({ history = [], onWithdraw }) => {
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleWithdrawClick = (consent) => {
    setSelectedConsent(consent);
    setReason('');
    setWithdrawModalOpen(true);
  };

  const confirmWithdraw = async (e) => {
    e.preventDefault();
    if (!reason) return;
    setSubmitting(true);
    try {
      await onWithdraw(selectedConsent.id, reason);
      setWithdrawModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatus = (consent) => {
    if (consent.withdrawn) return 'WITHDRAWN';
    return consent.granted ? 'GRANTED' : 'REFUSED';
  };

  return (
    <div className="consent-history card">
      <div className="card-header">
        <h2>Consent History</h2>
      </div>
      <div className="card-body">
        {history.length === 0 ? (
          <p className="empty-message">No consent records found.</p>
        ) : (
          <ul className="consent-timeline">
            {history.map((record) => {
              const typeInfo = CONSENT_CHECK_TYPES[record.type] || { label: record.type };
              const status = getStatus(record);

              return (
                <li key={record.id} className="timeline-item">
                  <div className="timeline-content">
                    <div className="timeline-header">
                      <span className="timeline-title">{typeInfo.label}</span>
                      <StatusBadge 
                        status={status === 'GRANTED' ? 'ACTIVE' : status === 'REFUSED' ? 'REJECTED' : 'SUSPENDED'} 
                        text={status} 
                      />
                    </div>
                    <div className="timeline-details">
                      <span className="timeline-date">{formatDateTime(record.date)}</span>
                      <span className="timeline-version">Notice v{record.noticeVersion}</span>
                    </div>
                    {status === 'GRANTED' && (
                      <button 
                        className="btn-withdraw" 
                        onClick={() => handleWithdrawClick(record)}
                      >
                        Withdraw Consent
                      </button>
                    )}
                    {record.withdrawnReason && (
                      <p className="timeline-reason">Withdrawal Reason: {record.withdrawnReason}</p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Modal
        isOpen={withdrawModalOpen}
        onClose={() => !submitting && setWithdrawModalOpen(false)}
        title="Withdraw Consent"
      >
        <form onSubmit={confirmWithdraw}>
          <p className="body-text" style={{ marginBottom: '16px' }}>
            Are you sure you want to withdraw consent for <strong>{selectedConsent && (CONSENT_CHECK_TYPES[selectedConsent.type]?.label || selectedConsent.type)}</strong>?
            This may pause or cancel your application verification process.
          </p>
          <FormField
            label="Reason for withdrawal"
            name="reason"
            type="textarea"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '24px' }}>
            <button type="button" className="btn-secondary" onClick={() => setWithdrawModalOpen(false)} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" style={{ backgroundColor: 'var(--danger)' }} disabled={submitting || !reason}>
              {submitting ? 'Withdrawing...' : 'Confirm Withdrawal'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

ConsentHistory.propTypes = {
  history: PropTypes.array.isRequired,
  onWithdraw: PropTypes.func.isRequired
};

export default ConsentHistory;
